/**
 * PRODUCT_AREAS registry (mission report-ux-professionalisation, WP02/T004;
 * contracts/content-registries.md "Navigation" — shape is authoritative,
 * transcribed from `./types.ts`). Data-driven sidebar: `AppShell` renders
 * the product-area part of its navigation purely from this array. Ships
 * `household` (labelled "London's Waste Dashboard" in the UI — id kept, see
 * below), `report` (labelled "Waste Capacity Study" — likewise) and
 * `benchmarking` (all users), `configuration` and `admin`
 * (super_admin only). `benchmarking` (product part 2: benchmarking + filtering
 * with complete drill-down) ships its three built-and-tested surfaces — the
 * mission-016 benchmarking-entity-layer post-merge review found them
 * unreachable because no WP owned this registry. `scenario-planning` /
 * `composition` remain TYPE-only (`ProductAreaId`) documented headroom for
 * future product areas — shipping data for THEM ahead of their feature is a
 * defect (FR-001: no dead links). A coverage test asserts this array's shipped
 * ids are exactly `{household, report, benchmarking, configuration, admin}`,
 * that `household` is FIRST, and that every href resolves to a real route.
 *
 * ORDER IS PRODUCT, NOT HOUSEKEEPING. The array's order is the sidebar's order,
 * and the first area is the product's front door — the one `app/page.tsx` lands
 * a reader on after sign-in. Reordering this array is a product decision
 * (mission londons-waste-dashboard-01M2NNJ1, WP01/T001, FR-001/FR-002).
 *
 * Report's per-section items nest their SUBSECTIONS (mission
 * report-nav-and-narrative-01KXE9QP, WP02/T004) — see `REPORT_ITEMS` below.
 */

import { displayHeading } from "@/features/report/components/subsectionDisplay";
import { REPORT_SECTIONS } from "@/features/report/sections";
import { SUBSECTIONS } from "@/features/report/subsections";
import type { ProductArea, ProductAreaNavItem } from "./types";

/**
 * Report's nav items are DERIVED from `REPORT_SECTIONS` and `SUBSECTIONS` — never duplicated by
 * hand. "Contents" (the /report index) always leads, as a plain link with no children of its
 * own. One link per section follows, in registry order — each carrying its OWN subsections
 * (`SUBSECTIONS[section.slug]`, in registry/document order) as nested hash-href items;
 * `AppShell` renders these as a Cloudscape `link-group` (mission report-nav-and-narrative-01KXE9QP,
 * WP02/T004; supersedes the mission-010/WP07 flat-link derivation this module used to carry —
 * every section link used to be a leaf, now every section link is a group).
 *
 * `label` uses `displayHeading` — the study's own numbering (verbatim in `studyHeading`, kept
 * for SC-001's PDF comparison) is stripped for display ONLY, here and nowhere else in the
 * registry itself (binding product ruling, Neil, 2026-07-13: see `subsectionDisplay.ts`'s
 * module docstring). A section added to `REPORT_SECTIONS` (with a corresponding `SUBSECTIONS`
 * entry) gets a nav entry automatically (mission 010, WP07/T026's interim-fix derivation,
 * preserved here inside the new registry per this WP's brief).
 */
const REPORT_ITEMS: ProductAreaNavItem[] = [
  { label: "Contents", href: "/report" },
  ...REPORT_SECTIONS.map(
    (section): ProductAreaNavItem => ({
      label: section.title,
      href: `/report/${section.slug}`,
      items: SUBSECTIONS[section.slug].map(
        (subsection): ProductAreaNavItem => ({
          label: displayHeading(subsection.studyHeading),
          href: `/report/${section.slug}#${subsection.id}`,
        }),
      ),
    }),
  ),
];

export const PRODUCT_AREAS: ProductArea[] = [
  {
    // FIRST, deliberately (mission londons-waste-dashboard-01M2NNJ1, WP01/T001; FR-001). This is
    // the product's front door: the top-line picture of London's waste — what arises, what it is
    // made of, how it is managed, whether there is enough infrastructure — and the area
    // `app/page.tsx` lands a reader on after sign-in (FR-002). It used to sit third, behind the
    // section-by-section capacity study, which meant the summary a strategic planner opens the
    // platform for was three levels down. Moving it back is a product change, not a tidy-up, and
    // `__tests__/productAreas.test.tsx` pins index 0 so it cannot happen by accident.
    //
    // The id stays "household" — the route paths (`/household`, `/household/targets`,
    // `/household/borough-performance`), route guards, help-panel keys and tests all key off it,
    // and there is precedent: `report` ships under the label "Waste Capacity Study" below. Only
    // the human-facing label changed, from "Household & Composition" (the name is client
    // decision D1, 2026-09-16; keeping the id is decision D in the same plan's open list —
    // docs/design/london-waste-dashboard-plan.md §0b and §8).
    //
    // Content still grows into the name: Re-London slice 1's recycling surfaces are here today on
    // already-primary WDF data; the headline arisings, composition, management-method, capacity
    // and self-sufficiency sections land on the same single scrolling page (client decision D2 —
    // anchors, not separate routes). See docs/design/london-waste-dashboard-plan.md §1 and
    // docs/methodology/re-london-brief.md.
    id: "household",
    // Doug review v2, 2026-09: "Rename: London's waste management dashboard". The page is
    // about how London MANAGES its waste, not only how much it has.
    label: "London's waste management dashboard",
    visibility: "all",
    // "Dashboard" (out-of-map wiring edit, mission londons-waste-dashboard, WP03/T011, declared
    // per this registry's own one-liner convention). `/household` used to be listed as
    // "Recycling rates" because that was the whole of its content. It is now the mission's
    // single scrolling page — arisings, composition, capacity and gap, self-sufficiency and the
    // recycling rate, each an anchored section — so naming the area's landing item after one of
    // its five measures sent a reader looking for arisings past the only link that reaches
    // them. One label, one line; the href, the id and the two sibling routes are unchanged.
    /* "Borough performance" (`/household/borough-performance`) IS GONE from this list, and the
       route with it (Doug, call notes 2026-09-24).

       It was the per-borough RECYCLING page. The dashboard now carries a section called
       "Performance by borough" which means something different — apportionment against the
       borough groups' targets — so the sidebar was offering two near-identically named things,
       one page and one section, measuring different quantities on different bases. A planner
       picking the wrong one would read a recycling rate as if it were apportionment
       performance.

       The COMPONENT is not deleted: `BoroughPerformancePage` still mounts at
       `/archive/borough-performance`, so nothing is lost and the surface can come back under a
       name that does not collide. */
    items: [
      { label: "Dashboard", href: "/household" },
      { label: "Targets", href: "/household/targets" },
    ],
  },
  {
    // id stays "report" (routes, AppShell special-casing, and tests key off the id); only the
    // human-facing sidebar label changes to the product's public name (Neil, mission 016
    // post-merge sidebar-IA fix). The /report route, the report page's own H1/content, and the
    // "Report" breadcrumb (which reflects the route) are deliberately unchanged.
    id: "report",
    label: "Waste Capacity Study",
    // GLA soft-launch (call notes, 2026-09-23; Neil's ruling the same day): the GLA view is
    // stripped to the dashboard and its two sibling pages, and the report is held back to RF
    // internal. It is not unfinished — it is the RF-facing reproduction of the study, and it
    // carries a table reference on every artefact, which is exactly the "presentation clutter"
    // the brief asks to take off the client-facing surface. Gating the AREA is what removes
    // every one of those references from a planner's view in one move, rather than teaching a
    // dozen components to hide their own citation.
    //
    // This gates the NAV ONLY. Artefact-level entitlement is unchanged and still enforced
    // server-side (ArtefactSpec.roles); a GLA reader who is handed a deep link still gets
    // exactly what they were always entitled to. Restoring the area to the GLA view is this
    // one word.
    visibility: "super_admin",
    items: REPORT_ITEMS,
  },
  {
    // Product part 2 (Neil's 4-part product): benchmarking + filtering with complete drill-down.
    // Planner-facing → visibility "all" (like Report), NOT super_admin. The three flagship
    // surfaces were built/tested/deep-linkable in mission 016 but had no nav entry; wiring them
    // here is what makes them reachable (post-merge review finding). Two paramless routes ship as
    // top-level links; the decision-cohort surface (`/entities/cohorts/decision`) needs
    // `?domain=&value=` and has no paramless landing, so it is reached by `decisionCohortHref`
    // from a decision cell (features/entity-links/entityHrefs.ts), never a dead top-level link
    // (FR-001).
    id: "benchmarking",
    label: "Benchmarking",
    // GLA soft-launch (call notes, 2026-09-23): held back with the report, for a different
    // reason — the brief names three core areas and benchmarking is not one of them, and
    // `Explore` in particular opens onto the full filter surface, which is the "drill-down into
    // unfinished sections" the brief asks to close off. The surfaces themselves work and stay
    // reachable for RF internal, which is where the meeting will demonstrate them from.
    visibility: "super_admin",
    items: [
      { label: "Benchmarking", href: "/benchmark" },
      // "League table" (out-of-map wiring edit, benchmark-league-table lane) — the
      // ranked all-33-borough leaderboard at `/benchmark/league`, reusing the same
      // benchmarking-compare data layer as the pick-2-and-compare tool above.
      { label: "League table", href: "/benchmark/league" },
      { label: "Explore", href: "/explore" },
    ],
  },
  {
    id: "configuration",
    label: "Configuration",
    visibility: "super_admin",
    // "Facility register" (out-of-map wiring edit, mission config-usability-01KXKPR7/WP02, per
    // that WP's own brief: "if it needs an out-of-map one-liner in a shared nav registry,
    // declare it") — the new register/decision-detail screens under `/admin/config/register`.
    // "Curator's handbook" / "Pending decisions" (out-of-map wiring edit, mission
    // config-usability-01KXKPR7/WP06, same declared-one-liner convention) — the new
    // `/admin/config/handbook` and `/admin/config/pending` screens.
    // "Vintage worklist" (out-of-map wiring edit, mission
    // config-usability-01KXKPR7/WP05, same declared-one-liner convention) — the new
    // `/admin/config/worklist` vintage-build queue.
    items: [
      { label: "Configuration", href: "/admin/config" },
      { label: "Facility register", href: "/admin/config/register" },
      { label: "Vintage worklist", href: "/admin/config/worklist" },
      { label: "Curator's handbook", href: "/admin/config/handbook" },
      { label: "Pending decisions", href: "/admin/config/pending" },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    visibility: "super_admin",
    // Whatever the pre-existing admin group carried, minus Configuration
    // (now its own product area above).
    items: [
      { label: "Tenants", href: "/admin/tenants" },
      { label: "Registry Explorer", href: "/admin/registry" },
      { label: "Data Access", href: "/data-access" },
      { label: "About", href: "/about" },
    ],
  },
];
